/**
 * Deterministic Tailwind class lookups for application state UI.
 * Use full literal strings so Tailwind's content scanner includes only these utilities.
 * Do not build class names with string concatenation or template literals.
 *
 * Colours follow JobCrawls public jc / earth-tone palette (forest, gold, muted red).
 */

export type StateButtonColor = 'purple' | 'green' | 'red' | 'emerald' | 'blue';

/** Button background/border classes for application state (ellipsis / state chip). */
export const STATE_BUTTON_COLOR_CLASSES: Record<StateButtonColor | 'default', string> = {
  purple:
    'border bg-[#F5EDD0]/90 dark:bg-[#2A1E08]/90 border-[#B8860B]/30 dark:border-[#D4A820]/25',
  green:
    'border bg-[#C8DDCE]/85 dark:bg-[#1A2E22]/90 border-[#3B5E45]/35 dark:border-[#5A8A68]/35',
  red: 'border bg-red-50 dark:bg-red-900/25 border-red-200 dark:border-red-800',
  emerald:
    'border bg-[#D8EADB]/90 dark:bg-[#1A2E22]/90 border-[#3B5E45]/30 dark:border-[#5A8A68]/35',
  blue:
    'border bg-jc-accent-muted/90 dark:bg-[#1A2E22]/90 border-jc-accent/30 dark:border-jc-accent/35',
  default: 'border bg-white/80 dark:bg-[#2A1D0F]/90 border-[#DDD5C8] dark:border-[#4A3520]',
};

/** Icon text color classes for application state. */
export const STATE_ICON_COLOR_CLASSES: Record<StateButtonColor | 'default', string> = {
  purple: 'text-[#6E4F00] dark:text-[#D4A820]',
  green: 'text-[#3B5E45] dark:text-[#98CFA8]',
  red: 'text-red-700 dark:text-red-300',
  emerald: 'text-[#2A4533] dark:text-[#98CFA8]',
  blue: 'text-[#3B5E45] dark:text-[#98CFA8]',
  default: 'text-[#7A6E5F] dark:text-[#8A7060]',
};

export type ApplicationStateLabelValue = 'interviewing' | 'offer' | 'rejected' | 'hired' | 'applied' | '';

/** Select/label border and text color per application state. */
export const STATE_LABEL_CLASSES: Record<ApplicationStateLabelValue, string> = {
  interviewing:
    'text-[#6E4F00] dark:text-[#D4A820] border-[#B8860B]/40 dark:border-[#D4A820]/35',
  offer:
    'text-[#3B5E45] dark:text-[#98CFA8] border-[#3B5E45]/40 dark:border-[#5A8A68]/40',
  rejected: 'text-red-700 dark:text-red-300 border-red-300 dark:border-red-800',
  hired:
    'text-[#2A4533] dark:text-[#98CFA8] border-[#3B5E45]/40 dark:border-[#5A8A68]/40',
  applied:
    'text-[#3B5E45] dark:text-[#98CFA8] border-[#3B5E45]/40 dark:border-[#98CFA8]/35',
  '': 'text-jc-ink-secondary border-jc-border',
};

export function getStateLabelClass(stateVal: string | null | undefined, isApplied: boolean): string {
  if (stateVal === 'interviewing' || stateVal === 'offer' || stateVal === 'rejected' || stateVal === 'hired') {
    return STATE_LABEL_CLASSES[stateVal as keyof typeof STATE_LABEL_CLASSES];
  }
  if (isApplied) return STATE_LABEL_CLASSES.applied;
  return STATE_LABEL_CLASSES[''];
}

/** Hit card / compact menu trigger surface (when a state colour is active). */
export function getHitStateButtonSurfaceClass(color: string): string {
  const key = color as keyof typeof STATE_BUTTON_COLOR_CLASSES;
  return STATE_BUTTON_COLOR_CLASSES[key] ?? STATE_BUTTON_COLOR_CLASSES.default;
}

/** Icon colour for state glyph on hit menu button. */
export function getHitStateIconClass(color: string): string {
  const key = color as keyof typeof STATE_ICON_COLOR_CLASSES;
  return STATE_ICON_COLOR_CLASSES[key] ?? STATE_ICON_COLOR_CLASSES.default;
}
